import React, {
  createContext,
  useContext,
  useState,
  useRef,
  ReactNode,
  useCallback,
  useEffect,
} from "react";

interface PodcastPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  currentPodcast: string | null;
  currentPodcast2: string | null;
  title: string;
  subsectionId: string | null;
  isCurrentPodcastVisible: boolean;
  selectedVoice: "male" | "female";
  currentPlayingUrl: string | null;
  currentSubsectionId: string | null;
}

interface PodcastPlayerContextType {
  playerState: PodcastPlayerState;
  playPodcast: (
    podcastUrl: string,
    title: string,
    subsectionId: string,
    podcastUrl2?: string
  ) => void;
  pausePodcast: () => void;
  setCurrentTime: (time: number) => void;
  setVolume: (volume: number) => void;
  setDuration: (duration: number) => void;
  setCurrentPodcastVisibility: (visible: boolean) => void;
  switchVoice: (voiceUrl: string) => void;
  setSelectedVoice: (voice: "male" | "female") => void;
  notifyAudioProgress: (currentTime: number, duration: number) => void;
  //getAudioElement: () => HTMLAudioElement | null; // Nuevo
}

const PodcastPlayerContext = createContext<
  PodcastPlayerContextType | undefined
>(undefined);

export const PodcastPlayerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [playerState, setPlayerState] = useState<PodcastPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    currentPodcast: null,
    currentPodcast2: null,
    title: "",
    subsectionId: null,
    isCurrentPodcastVisible: true,
    selectedVoice: "male",
    currentPlayingUrl: null,
    currentSubsectionId: "",
  });

  const audioRef = useRef<HTMLAudioElement>(new Audio());

  const setCurrentPodcastVisibility = useCallback((visible: boolean) => {
    setPlayerState((prev) => ({
      ...prev,
      isCurrentPodcastVisible: visible,
    }));
  }, []);

  const notifyAudioProgress = useCallback(
    (currentTime: number, duration: number) => {
     
    },
    []
  );

  const setSelectedVoice = useCallback((voice: "male" | "female") => {
    setPlayerState((prev) => ({ ...prev, selectedVoice: voice }));
  }, []);

  // Exponer el elemento de audio
  const getAudioElement = useCallback(() => {
    return audioRef.current;
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setPlayerState((prev) => ({ ...prev, currentTime: audio.currentTime }));
    };

    const handleLoadedMetadata = () => {
      setPlayerState((prev) => ({ ...prev, duration: audio.duration }));
    };

    const handleEnded = () => {
      setPlayerState((prev) => ({ ...prev, isPlaying: false }));
    };

    const handlePlay = () => {
      setPlayerState((prev) => ({ ...prev, isPlaying: true }));
    };

    const handlePause = () => {
      setPlayerState((prev) => ({ ...prev, isPlaying: false }));
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    audio.volume = playerState.volume;
    audio.preload = "metadata";

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.pause();
    };
  }, []);

  useEffect(() => {
    audioRef.current.volume = playerState.volume;
  }, [playerState.volume]);

  const playPodcast = useCallback(
    async (
      podcastUrl: string,
      title: string,
      subsectionId: string,
      podcastUrl2?: string
    ) => {
      const audio = audioRef.current;

      try {
        const urlToPlay =
          playerState.selectedVoice === "female" && podcastUrl2
            ? podcastUrl2
            : podcastUrl;

        if (playerState.currentPlayingUrl !== urlToPlay) {
          audio.src = urlToPlay;
          audio.load();
          setPlayerState((prev) => ({
            ...prev,
            currentPodcast: podcastUrl,
            currentPodcast2: podcastUrl2 || null,
            currentPlayingUrl: urlToPlay,
            title,
            subsectionId,
            currentSubsectionId: subsectionId,
            currentTime: 0,
            duration: 0,
            isCurrentPodcastVisible: true,
          }));
        } else {
          setPlayerState((prev) => ({
            ...prev,
            title,
            subsectionId,
            currentSubsectionId: subsectionId,
            ...(podcastUrl2 && { currentPodcast2: podcastUrl2 }),
          }));
        }

        await audio.play();
        setPlayerState((prev) => ({ ...prev, isPlaying: true }));
      } catch (error) {
        console.error("Error playing podcast:", error);
        setPlayerState((prev) => ({ ...prev, isPlaying: false }));
      }
    },
    [playerState.selectedVoice, playerState.currentPlayingUrl]
  );

  const pausePodcast = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    setPlayerState((prev) => ({ ...prev, isPlaying: false }));
  }, []);

  const switchVoice = useCallback(
    async (voiceUrl: string) => {
      const audio = audioRef.current;
      const wasPlaying = playerState.isPlaying;
      const currentTime = playerState.currentTime;

      try {
        if (audio.src !== voiceUrl) {
          if (wasPlaying) {
            audio.pause();
          }

          audio.src = voiceUrl;
          audio.currentTime = currentTime;
          audio.load();

          setPlayerState((prev) => ({ ...prev, currentPlayingUrl: voiceUrl }));

          if (wasPlaying) {
            await audio.play();
          }
        }
      } catch (error) {
        console.error("Error switching voice:", error);
      }
    },
    [playerState.isPlaying, playerState.currentTime]
  );

  const setCurrentTime = useCallback((time: number) => {
    const audio = audioRef.current;
    audio.currentTime = time;
    setPlayerState((prev) => ({ ...prev, currentTime: time }));
  }, []);

  const setVolume = useCallback((volume: number) => {
    setPlayerState((prev) => ({ ...prev, volume }));
  }, []);

  const setDuration = useCallback((duration: number) => {
    setPlayerState((prev) => ({ ...prev, duration }));
  }, []);

  const value: PodcastPlayerContextType = {
    playerState,
    playPodcast,
    pausePodcast,
    setCurrentTime,
    setVolume,
    setDuration,
    setCurrentPodcastVisibility,
    switchVoice,
    setSelectedVoice,
    notifyAudioProgress,
    //getAudioElement, // Incluido en el contexto
  };

  return (
    <PodcastPlayerContext.Provider value={value}>
      {children}
    </PodcastPlayerContext.Provider>
  );
};

export const usePodcastPlayer = () => {
  const context = useContext(PodcastPlayerContext);
  if (!context) {
    throw new Error(
      "usePodcastPlayer must be used within PodcastPlayerProvider"
    );
  }
  return context;
};